// reportExtension.ts
import StarterKit from '@tiptap/starter-kit';
import { Mark, mergeAttributes } from '@tiptap/core';
import { Color } from '@tiptap/extension-color';
import Document from '@tiptap/extension-document';
import ListItem from '@tiptap/extension-list-item';
import TextStyle from '@tiptap/extension-text-style';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import Heading from '@tiptap/extension-heading';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Highlight from '@tiptap/extension-highlight';
import FontFamily from '@tiptap/extension-font-family';

/** This allows a plain `<span>` (but not any attributes on it) */
export interface SpanOptions {
  HTMLAttributes: Record<string, any>;
}
export const Span = Mark.create<SpanOptions>({
  name: 'span',
  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      customClass: {
        default: null,
        parseHTML: element => element.getAttribute('data-class'),
        renderHTML: attributes => {
          return {
            class: attributes.customClass || '',
          };
        },
      },
      // Add the second customClass
      additionalClass: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attributes => {
          // This will not sanitize the class, allowing any value to be applied
          return {
            class: attributes.additionalClass || '',
          };
        },
      },
      inlineStyle: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => {
          if (!attributes.inlineStyle) return {};
          return { style: attributes.inlineStyle };
        },
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span',
        getAttrs: element => {
          return {};
        },
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
});

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      // extend the existing attributes …
      ...this.parent?.(),

      // and add a new one …
      backgroundColor: {
        default: null,
        parseHTML: element => element.getAttribute('data-background-color'),
        renderHTML: attributes => {
          return {
            'data-background-color': attributes.backgroundColor,
            style: `background-color: ${attributes.backgroundColor}`,
          };
        },
      },
    };
  },
});

const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      customClass: {
        default: null,
        parseHTML: element => element.getAttribute('data-class'),
        renderHTML: attributes => {
          return {
            class: attributes.customClass || '',
          };
        },
      },
      // Add the second customClass
      additionalClass: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attributes => {
          // This will not sanitize the class, allowing any value to be applied
          return {
            class: attributes.additionalClass || '',
          };
        },
      },
    };
  },
});

const CustomHeader = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      customClass: {
        default: null,
        parseHTML: element => element.getAttribute('data-class'),
        renderHTML: attributes => {
          return {
            class: attributes.customClass || '',
          };
        },
      },
      // Add the second customClass
      additionalClass: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attributes => {
          // This will not sanitize the class, allowing any value to be applied
          return {
            class: attributes.additionalClass || '',
          };
        },
      },
    };
  },
});

const EditableWordMark = Mark.create({
  name: 'editableWord',
  addAttributes() {
    return {
      text: { default: null },
      class: { default: null },
      'data-class': { default: null },
      /**
       * Who owns the words in this slot. BOTH of these persist in the saved report,
       * and that is the whole point of them.
       *
       * Before this existed, the wording engine decided "has a human touched this?" by
       * comparing the slot against a baseline snapshot taken when the report was opened.
       * That works within one sitting and fails completely across two: on reopen the
       * baseline is re-read from the saved document, so a correction the tech made and
       * saved yesterday is indistinguishable from a template default, and the engine
       * overwrites it. Dr Shashi, 06 Sep: *"echo tech is not aware of these changes. So
       * when they reporting and moving to next patient smart reporting changing it
       * back"*, and *"if tech or doctors changing any report smart report system should
       * not override it"*.
       *
       * `data-user="1"` — a clinician chose these words. The engine never writes to this
       * slot again, on this open or any future one. Permanent, and deliberately so: a
       * report is a clinical document and the human's wording stands.
       *
       * `data-auto` — the exact text the engine last wrote here, including its casing.
       * The engine may rewrite a slot only while the slot still reads what the engine
       * itself put there. The moment the text differs, someone edited it by hand and the
       * slot is locked, even if `data-user` was never set (a typed-over value rather
       * than a dropdown pick).
       *
       * A slot carrying NEITHER attribute is an untouched template default, which is the
       * only state the engine treats as free to fill. That inference is sound because the
       * engine runs on open, before a clinician can type anything.
       */
      'data-user': { default: null },
      'data-auto': { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-editable]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-editable': '',
        style: `
          cursor: pointer;
          border-bottom: 2px dashed skyblue;
          position: relative;
          transition: all 0.2s ease-in-out;
          padding: 0 2px;
        `,
        onmouseenter:
          "this.style.backgroundColor='rgba(135, 206, 235, 0.4)'; this.style.borderBottom='1.5px dashed #3498db'",
        onmouseleave:
          "this.style.backgroundColor='transparent'; this.style.borderBottom='1.5px dashed skyblue'",
      }),
      0,
    ];
  },
});

/**
 * Numeric report slot — a measured value the clinician clicks and types into.
 *
 * Deliberately a separate attribute (`data-editable-number`) rather than a flag
 * on `data-editable`: the Report Dropdowns admin page builds its slot list by
 * querying `span[data-editable]`, and numeric fields have no option list to
 * manage, so they must not appear there.
 *
 * Styled with a dotted amber underline so it reads as "type a value here",
 * visibly distinct from the dashed blue "pick a phrase here".
 */
const EditableNumberMark = Mark.create({
  name: 'editableNumber',
  addAttributes() {
    return {
      class: { default: null },
      'data-class': { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-editable-number]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-editable-number': '',
        style: `
          cursor: pointer;
          border-bottom: 2px dotted #d19a2a;
          position: relative;
          transition: all 0.2s ease-in-out;
          padding: 0 2px;
        `,
        onmouseenter:
          "this.style.backgroundColor='rgba(209, 154, 42, 0.25)'; this.style.borderBottom='1.5px dotted #b8801a'",
        onmouseleave:
          "this.style.backgroundColor='transparent'; this.style.borderBottom='2px dotted #d19a2a'",
      }),
      0,
    ];
  },
});

/**
 * A measurement value that participates in field chaining.
 *
 * Same shape as `EditableNumberMark` above — a span whose FIRST class is the
 * report data key (`LVIDd`, `bsa`, `RVSP`, …), which is how the value is found
 * again after it has been rendered into the document. Chaining needs exactly
 * that: an identity to walk to when something it derives from changes.
 *
 * Kept separate from `data-editable-number` on purpose. The stress numeric
 * slots are free-typed values with no relationships; these are the measurement
 * values with formulas behind them, they are styled differently (teal, so a
 * clinician can see at a glance which numbers move together), and the Report
 * Dropdowns admin page must not list either of them.
 *
 * `data-chain-warning` is set when a value has been edited mid-chain and no
 * longer agrees with the values it is derived from (e.g. a BSA that no longer
 * matches Height/Weight). Advisory only — the edit stands and the chain below
 * it still runs; the field just underlines amber and says why on hover.
 */
const ChainFieldMark = Mark.create({
  name: 'chainField',
  // ProseMirror's default (true) extends the mark onto whatever is typed right at its
  // edge. Numeric fields have a static unit sitting outside the mark as a natural gap,
  // but EF_Prose is freeform with no fixed trailing character — without this, typing
  // straight after it is absorbed into the value and copied to its other occurrence on
  // the next chain update, the exact bug Nikhil reported for the "%" case.
  inclusive: false,
  addAttributes() {
    return {
      class: { default: null },
      title: { default: null },
      'data-chain-warning': {
        default: null,
        parseHTML: element => element.getAttribute('data-chain-warning'),
        renderHTML: attributes =>
          attributes['data-chain-warning']
            ? { 'data-chain-warning': attributes['data-chain-warning'] }
            : {},
      },
    };
  },
  parseHTML() {
    // The catch-all `Span` mark (`tag: 'span'`, no attribute constraint) has
    // the same default priority, and on a tie the later rule can win — an
    // explicit boost keeps a saved chain field from reparsing as an inert span
    // and losing its key.
    return [{ tag: 'span[data-chain-field]', priority: 100 }];
  },
  renderHTML({ HTMLAttributes }) {
    const warning = HTMLAttributes['data-chain-warning'];
    const accent = warning ? '#d19a2a' : '#2dd4bf';
    const accentHover = warning ? '#b8801a' : '#14b8a6';
    const accentTint = warning ? 'rgba(209, 154, 42, 0.25)' : 'rgba(45, 212, 191, 0.25)';

    /**
     * Nikhil, 1 Sep: "is it possible to remove all blue lines from down below? In table".
     * Every measurement in the table carried a teal dashed underline, so a report of forty
     * values read as forty underlined numbers.
     *
     * The underline is dropped AT REST and kept ON HOVER. It was doing a real job —
     * showing which numbers can be clicked and retyped — and deleting it outright would
     * leave no way to discover that at all. Hovering still tints the value and underlines
     * it, and the pointer cursor is unchanged, so the affordance survives while the
     * printed-looking report stays clean.
     *
     * THE AMBER WARNING STATE IS NOT DECORATION AND STAYS VISIBLE. It means this value no
     * longer agrees with the values it was derived from — a BSA that no longer matches
     * height and weight, say. That is a clinical signal a reader must not have to hover to
     * find, so only the teal resting underline goes.
     *
     * Transparent rather than `none`: the border still occupies its 2px, so a value does
     * not change height when the hover underline appears and the table row cannot jitter
     * as the pointer crosses it.
     */
    const restingBorder = warning ? `2px dashed ${accent}` : '2px dashed transparent';

    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-chain-field': '',
        style: `
          cursor: pointer;
          border-bottom: ${restingBorder};
          position: relative;
          transition: all 0.2s ease-in-out;
          padding: 0 2px;
        `,
        onmouseenter: `this.style.backgroundColor='${accentTint}'; this.style.borderBottom='1.5px dashed ${accentHover}'`,
        onmouseleave: `this.style.backgroundColor='transparent'; this.style.borderBottom='${restingBorder}'`,
      }),
      0,
    ];
  },
});

/**
 * Dynamic fields — user-created synced text, as opposed to `chainField`'s
 * fixed, developer-registered measurement set (see the 17 Sep vault note,
 * "Dynamic fields" §12/13). A clinic user names one from the template
 * editor's Insert Field button; every span sharing its `class` (the
 * generated key) stays in sync, same mechanism as `chainField`, deliberately
 * a SEPARATE mark rather than reusing chainField's — dynamic fields carry no
 * unit/formula/CHAIN_FIELDS entry, so they must never enter the measurement
 * chain graph or its %-boundary/freeform special-casing.
 *
 * Violet accent (vs. chainField's teal/amber) so a clinician can tell at a
 * glance which underlined values are the fixed measurement set and which are
 * a field this clinic added themselves.
 */
const DynamicFieldMark = Mark.create({
  name: 'dynamicField',
  inclusive: false,
  addAttributes() {
    return {
      class: { default: null },
      title: { default: null },
      // v2 (18 Sep): field type + its type-specific data. 'text' (default) behaves
      // exactly as v1. 'dropdown' carries its clinic-defined option list as JSON in
      // data-options. 'number' carries its unit in data-unit, same idea as
      // CHAIN_FIELDS units but never entered into CHAIN_FIELDS itself.
      'data-field-type': { default: 'text' },
      'data-options': { default: null },
      'data-unit': { default: null },
      // 18 Sep — the exact raw SR concept name (CodeMeaning) this field should
      // catch at report-open, e.g. "XAEA12". Never matched via measurementAliases.ts;
      // see findSRValueByExactConceptName in helper.ts.
      'data-sr-concept': { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-dynamic-field]', priority: 100 }];
  },
  renderHTML({ HTMLAttributes }) {
    // A dropdown field looks like the template's own phrase dropdowns.
    if (HTMLAttributes['data-field-type'] === 'dropdown') {
      return [
        'span',
        mergeAttributes(HTMLAttributes, {
          'data-dynamic-field': '',
          style: `
            cursor: pointer;
            border-bottom: 2px dashed skyblue;
            position: relative;
            transition: all 0.2s ease-in-out;
            padding: 0 2px;
          `,
          onmouseenter:
            "this.style.backgroundColor='rgba(135, 206, 235, 0.4)'; this.style.borderBottom='1.5px dashed #3498db'",
          onmouseleave:
            "this.style.backgroundColor='transparent'; this.style.borderBottom='1.5px dashed skyblue'",
        }),
        0,
      ];
    }
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-dynamic-field': '',
        style: `
          cursor: pointer;
          border-bottom: 2px dashed transparent;
          position: relative;
          transition: all 0.2s ease-in-out;
          padding: 0 2px;
        `,
        onmouseenter:
          "this.style.backgroundColor='rgba(167, 139, 250, 0.25)'; this.style.borderBottom='1.5px dashed #a78bfa'",
        onmouseleave:
          "this.style.backgroundColor='transparent'; this.style.borderBottom='2px dashed transparent'",
      }),
      0,
    ];
  },
});

export const extensions = [
  // Document removed — StarterKit provides the 'doc' node
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle.configure({ types: [ListItem.name] }),
  BubbleMenu.configure({
    element: document.querySelector('.bubble-menu'),
  }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
      HTMLAttributes: { class: 'editor-list-bullet' },
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
      HTMLAttributes: { class: 'editor-list-ordered' },
    },
    heading: false, // disabled — CustomHeader below provides 'heading' node
    bold: { HTMLAttributes: { class: 'editor-bold' } },
    italic: { HTMLAttributes: { class: 'editor-italic' } },
    paragraph: { HTMLAttributes: { class: 'editor-paragraph' } },
    code: { HTMLAttributes: { class: 'editor-code' } },
    strike: { HTMLAttributes: { class: 'editor-strike' } },
    codeBlock: { HTMLAttributes: { class: 'editor-code-block' } },
    horizontalRule: { HTMLAttributes: { class: 'editor-horizontal-rule' } },
    listItem: { HTMLAttributes: { class: 'editor-list-item' } },
    blockquote: { HTMLAttributes: { class: 'editor-blockquote' } },
  }),
  EditableWordMark,
  EditableNumberMark,
  ChainFieldMark,
  DynamicFieldMark,
  Span,
  CustomHeader,
  CustomTable,
  TableRow,
  TableHeader,
  // TableCell removed — CustomTableCell below provides 'tableCell' node
  CustomTableCell, // extends TableCell, keeps same name 'tableCell'
  /**
   * Alignment stays available from the toolbar; its keyboard shortcuts do not.
   *
   * TipTap binds Mod-Shift-L / E / R / J by default, which silently re-aligns whatever
   * block the cursor is in — no button pressed, no visible feedback, and autosave then
   * persists it into a clinical report. A screen-capture shortcut collided with
   * Mod-Shift-R and right-aligned the Conclusions heading and a measurement cell of a
   * live report on 26 Aug. Alignment already in a document is untouched; only the
   * invisible way of changing it is removed.
   */
  TextAlign.extend({
    addKeyboardShortcuts: () => ({}),
  }).configure({
    types: ['heading', 'paragraph'],
  }),
  Image.configure({
    inline: true,
    allowBase64: true,
  }),
  Link,
  Underline,
  Subscript,
  Superscript,
  Highlight.configure({ multicolor: true }),
  FontFamily,
];

export const wordSuggestionsMap = {
  'study-quality': ['Good', 'Average', 'Poor', 'Technically difficult'],
  'sinus-rhythm': [
    'Sinus',
    'Sinus Bradycardia',
    'Sinus Tachycardia',
    'Sinus with PVCs',
    'Sinus with PACs',
    'Atrial Fibrillation',
    'Sinus with RBBB',
    'Sinus with LBBB',
    'Junctional Rhythm',
    'Paced Rhythm',
  ],
  'lv-biplane': ["Simpson'\s Biplane", 'visual estimation'],
  'lv-normal-one': ['Normal', 'Mildly dilated', 'Moderately dilated', 'Severely dilated', 'Small'],
  'lv-normal-second': [
    'normal',
    'preserved',
    'mildly reduced',
    'moderately reduced',
    'severely reduced',
  ],
  'lv-normal-third': [
    'Normal',
    'Mild concentric increase in LV',
    'Moderalely increased Lv',
    'Severely increased LV',
    'Basal septal bulge measurina?cm with',
  ],
  'lv-normal-fourth': ['Normal', 'Grade I', 'Grade II', 'Grade III'],
  'lv-normal-fifth': ['Normal', 'Elevated'],
  'rv-normal': ['Normal', 'Mildly dilated', 'Moderately dilated', 'Severely dilated'],
  'rv-systolic-function': [
    'and systolic function',
    'with mildly reduced systolic function',
    'with moderately reduced systolic function',
    'with severely reduced systolic function',
    'with preserved systolic function',
  ],
  'la-normal': ['Normal', 'Mildly dilated', 'Moderately dilated', 'Severely dilated'],
  'la-septal-defect': [
    'Patent foramen ovale on colour flow Doppler with left to right shunt.',
    'There is a small secundum atrial septal defect measuring < 10 mm in diameter. Qp:Qs =',
    'There is a small primum atrial septal defect measuring < 10 mm in diameter. Qp:Qs =',
    'There is a small sinus venosus atrial septal defect measuring < 10 mm in diameter. Qp:Qs =',
  ],
  'ra-normal': ['Normal', 'Mildly dilated', 'Moderately dilated', 'Severely dilated'],
  'av-valve': ['Normal', 'Sclerotic', 'Calcified', 'Thickened'],
  'av-trileaflet': [
    'trileaflet',
    "bicuspid aortic valve with commissures at ? o'clock and ? o'clock and raphe at ? o'clock.",
    'unicuspid',
    'quadricuspid',
    'true bicuspid',
  ],
  'lq-mm-valve': [
    'There is a mm (type) (size and type) Bioprosthetic aortic valve replacement, which appears well seated.',
    'There is a mm (type) (size and type) Transcatheter aortic valve implantation/replacement, which appears well seated.',
  ],
  'la-no': [
    'No',
    'Trivial',
    'Mild',
    'Moderate',
    'Severe',
    'Mild to moderate',
    'Moderate to severe',
  ],
  'la-effective': [
    'Aortic valve area is -- cm², mean pressure gradient is -- mmHg, maximum velocity is -- m/s, stroke volume index is -- ml/m², and dimensionless performance index is -- .',
    'The effective orifice area is -- cm², mean pressure gradient is -- mmHg, maximum velocity is -- m/s, stroke volume index is -- ml/m², and dimensionless performance index is -- .',
  ],
  'mv-normal': [
    'Normal',
    'Thickened',
    'Sclerotic',
    'Rheumatic',
    'Myxomatous',
    'There is mitral valve replacement which appears well seated.',
  ],
  'mv-calcification': [
    'There is anterior/posterior mitral leaflet prolapse.',
    'There is anterior/posterior mitral annular calcification present.',
  ],
  'mv-trivial': [
    'No',
    'Trivial',
    'Mild',
    'Moderate',
    'Severe',
    'Mild to moderate',
    'Moderate to severe',
  ],
  'mv-no': [
    'No',
    'Trivial',
    'Mild',
    'Moderate',
    'Severe',
    'Mild to moderate',
    'Moderate to severe',
  ],
  'mv-mean-pressure': [
    'Mitral valve mean pressure gradient is mmHg.',
    'The mean pressure gradient is mmHg and the mitral valve area by method is cm².',
  ],
  'tv-trivial': [
    'No',
    'Trivial',
    'Mild',
    'Moderate',
    'Severe',
    'Mild to moderate',
    'Moderate to severe',
  ],
  'tv-mmhg': ['3', '8', '15'],
  'pv-trivial': ['No', 'Trivial', 'Mild', 'Moderate', 'Severe'],
  physiological: [
    'no evidence of',
    'trivial',
    'mild loculated',
    'mild circumferential',
    'moderate loculated',
    'moderate circumferential',
    'severe loculated',
    'severe circumferential',
    'trivial circumferential',
    'physiological',
  ],
};
